import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography,
} from "@mui/material";
import { actualizarSolicitud, buscarClientesEgresos, getCatalogosEgresos, getSolicitud } from "../services/egresosService";
import { fieldSx } from "./formStyles";
import { TituloSeccion } from "./ComprobanteUi";

// Las solicitudes guardan el mes como "Abril del 2026" (y algunas antiguas "Abril de 2026").
const MES_RE = /^(.+?) del? (\d{4})$/;

// Solo se elige de la lista; si el valor ya cargado no está en ella, se mantiene visible para no perderlo.
const opcionesNombre = (nombres = [], actual) => (actual && !nombres.includes(actual) ? [actual, ...nombres] : nombres);

// Modificar una solicitud de cheque que todavía no se emitió (mismo formulario que la edición del comprobante).
// Si se deja vacío el importe se conservan los montos actuales.
export default function SolicitudEditarDialog({ id, onClose, onGuardada }) {
  const [cat, setCat] = useState(null);
  const [s, setS] = useState(null);
  const [f, setF] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [clienteModo, setClienteModo] = useState("mantener"); // mantener | ninguno | cambiar
  const [cliente, setCliente] = useState(null);
  const [opcionesCliente, setOpcionesCliente] = useState([]);

  useEffect(() => {
    if (!id) { setS(null); setF(null); return; }
    setError(""); setClienteModo("mantener"); setCliente(null); setF(null);
    Promise.all([getCatalogosEgresos(), getSolicitud(id)])
      .then(([c, r]) => { setCat(c.data); setS(r.data); })
      .catch((e) => setError(e?.response?.data?.error || "No se pudo cargar la solicitud."));
  }, [id]);

  useEffect(() => {
    if (!s || !cat) return;
    const m = MES_RE.exec(String(s.mes || ""));
    setF({
      cambio: String(s.cambio ?? ""), dirigido: s.nombre || "", deposito: s.deposito || "", ciudad: s.ciudad || "", servicio: s.servicio || "",
      num_contrato: s.nro_contrato || "", num_referencia: s.nro_referencia || "", importe: "", moneda: s.moneda === "Dolares" ? "Dolares" : "Bolivianos",
      concepto: cat.conceptos.find((c) => c.concepto === s.concepto) || null,
      mesNombre: m ? m[1] : cat.meses[new Date().getMonth()], year: m ? Number(m[2]) : new Date().getFullYear(),
      observacion: s.obs || "", retencion: s.tipo_retencion || "",
    });
  }, [s, cat]);

  const set = (k) => (ev) => setF((p) => ({ ...p, [k]: ev.target.value }));
  const buscarCliente = (texto) => {
    if (texto.trim().length < 2) { setOpcionesCliente([]); return; }
    buscarClientesEgresos(texto.trim()).then((r) => setOpcionesCliente(r.data || [])).catch(() => setOpcionesCliente([]));
  };

  const years = useMemo(() => (f ? [-2, -1, 0, 1].map((d) => new Date().getFullYear() + d).concat(f.year).filter((v, i, a) => a.indexOf(v) === i).sort() : []), [f]);
  const conLista = (lista = [], actual) => (actual && !lista.includes(actual) ? [actual, ...lista] : lista);

  const guardar = async () => {
    if (clienteModo === "cambiar" && !cliente) { setError("Selecciona el cliente o elige otra opción."); return; }
    setGuardando(true);
    setError("");
    try {
      const body = {
        cambio: f.cambio, dirigido: f.dirigido, deposito: f.deposito, ciudad: f.ciudad, servicio: f.servicio, num_contrato: f.num_contrato,
        num_referencia: f.num_referencia, importe: f.importe, moneda: f.moneda, conceptoId: f.concepto?.id, mes: `${f.mesNombre} del ${f.year}`,
        observacion: f.observacion, retencion: f.retencion,
      };
      if (clienteModo === "ninguno") body.codServicioCliente = null;
      if (clienteModo === "cambiar") body.codServicioCliente = cliente.id;
      await actualizarSolicitud(id, body);
      onGuardada(`Solicitud ${id} modificada.`);
      onClose();
    } catch (er) {
      setError(er?.response?.data?.error || "No se pudo guardar la solicitud.");
    } finally {
      setGuardando(false);
    }
  };

  const campo = (k, label, size = { xs: 12, sm: 6, md: 3 }, extra = {}) => (
    <Grid size={size}><TextField fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx} {...extra} /></Grid>
  );
  const select = (k, label, opciones, size = { xs: 12, sm: 6, md: 3 }) => (
    <Grid size={size}>
      <TextField select fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx}>
        {opciones.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </TextField>
    </Grid>
  );

  return (
    <Dialog open={!!id} onClose={() => !guardando && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Modificar solicitud de cheque N° {id}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        {!f && !error && <CircularProgress size={20} />}
        {f && cat && (
          <Grid container spacing={0.75}>
            <TituloSeccion>Datos de la solicitud</TituloSeccion>
            {campo("cambio", "Tipo de cambio")}
            {select("ciudad", "Ciudad destino", conLista(cat.ciudades, f.ciudad))}
            {select("servicio", "Tipo de servicio", conLista(cat.servicios, f.servicio))}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" label="Estado" value={s.estado === "autorizado" ? "Aprobada" : "Pendiente"} disabled sx={fieldSx} />
            </Grid>

            <TituloSeccion>Beneficiario</TituloSeccion>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete size="small" options={opcionesNombre(cat.nombres, f.dirigido)} value={f.dirigido || null} onChange={(_, v) => setF((p) => ({ ...p, dirigido: v || "" }))} noOptionsText="Sin resultados: el nombre debe estar en la lista"
                renderInput={(p) => <TextField {...p} label="Dirigido a" sx={fieldSx} />} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete size="small" options={opcionesNombre(cat.nombres, f.deposito)} value={f.deposito || null} onChange={(_, v) => setF((p) => ({ ...p, deposito: v || "" }))} noOptionsText="Sin resultados: el nombre debe estar en la lista"
                renderInput={(p) => <TextField {...p} label="Depositado a" sx={fieldSx} />} />
            </Grid>

            <TituloSeccion>Importe y concepto</TituloSeccion>
            {campo("importe", "Importe (vacío = conservar)", { xs: 6, md: 2 })}
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField select fullWidth size="small" label="Moneda" value={f.moneda} onChange={set("moneda")} sx={fieldSx}>
                <MenuItem value="Bolivianos">Bolivianos</MenuItem>
                <MenuItem value="Dolares">Dólares</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete size="small" options={cat.conceptos} value={f.concepto} onChange={(_, v) => setF((p) => ({ ...p, concepto: v }))}
                getOptionLabel={(o) => o?.concepto || ""} isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(p) => <TextField {...p} label={f.concepto || !s.concepto ? "Concepto" : `Concepto (actual: ${s.concepto})`} sx={fieldSx} />} />
            </Grid>
            {select("mesNombre", "Mes de servicio", cat.meses, { xs: 6, md: 2.5 })}
            <Grid size={{ xs: 6, md: 1.5 }}>
              <TextField select fullWidth size="small" label="Año" value={f.year} onChange={set("year")} sx={fieldSx}>
                {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            {campo("num_contrato", "N° de contrato")}
            {campo("num_referencia", "N° de referencia")}

            <Grid size={12}><TextField fullWidth multiline minRows={2} maxRows={6} size="small" label="Observación" value={f.observacion} onChange={set("observacion")} slotProps={{ htmlInput: { maxLength: 250 } }} sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], py: "3px" } }} /></Grid>

            <TituloSeccion>Retención y cliente</TituloSeccion>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select fullWidth size="small" label="Retención" value={f.retencion} onChange={set("retencion")} sx={fieldSx}>
                <MenuItem value="">Sin retención</MenuItem>
                {cat.retenciones.map((r) => <MenuItem key={r.id} value={r.nombre}>{r.nombre} ({r.calculo}%)</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField select fullWidth size="small" label="Cliente" value={clienteModo} onChange={(ev) => setClienteModo(ev.target.value)} sx={fieldSx}>
                <MenuItem value="mantener">Mantener: {s.cliente || "sin cliente"}</MenuItem>
                <MenuItem value="ninguno">Sin cliente</MenuItem>
                <MenuItem value="cambiar">Cambiar cliente…</MenuItem>
              </TextField>
            </Grid>
            {clienteModo === "cambiar" && (
              <Grid size={{ xs: 12, md: 5 }}>
                <Autocomplete size="small" options={opcionesCliente} value={cliente} filterOptions={(x) => x}
                  getOptionLabel={(o) => `${o.razon_social} ${o.servicio}${o.descripcion ? ` - ${o.descripcion}` : ""}`} isOptionEqualToValue={(a, b) => a.id === b.id}
                  onInputChange={(_, v, reason) => { if (reason === "input") buscarCliente(v); }} onChange={(_, v) => setCliente(v)}
                  noOptionsText="Escribe para buscar un cliente" renderInput={(p) => <TextField {...p} label="Buscar cliente" sx={fieldSx} />} />
              </Grid>
            )}
          </Grid>
        )}
        <Typography sx={{ fontSize: 11, mt: 1 }} color="text.secondary">
          Si cambias la moneda o el tipo de cambio sin escribir el importe, se conservan los montos actuales en Bs y $us. La solicitud mantiene su estado.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button size="small" variant="contained" disabled={!f || guardando} onClick={guardar}>{guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  );
}
